import React, { useState, useEffect } from "react";
import { FaUserMd, FaLeaf, FaDumbbell } from "react-icons/fa";
import { ArrowRight } from "lucide-react";

const Pricing = ({ onOpenEnquiryForm }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkWidth();
    window.addEventListener('resize', checkWidth);
    
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  const pricingPlans = [
    {
      icon: <FaLeaf size={26} />,
      title: "1-MONTH HEALTHY EATING PROGRAM",
      subtitle: "Build healthy & sustainable eating habits.",
      price: "3999",
      priceDetails: "/30 days",
      features: [
        "Full assessment",
        "Personalised diet chart",
        "Diet explanation call",
        "Weekly check-ins",
        "Recipes & meal swaps",
        "Plan modifications",
      ],
      idealFor: "Anyone who wants consistency, discipline & better habits.",
      buttonText: "JOIN 1-MONTH PLAN",
      isFeatured: true,
    },
    {
      icon: <FaUserMd size={26} />,
      title: "ONE-TIME NUTRITION CONSULTATION",
      subtitle: "Quick expert guidance tailored for you.",
      price: "699",
      priceDetails: "/session",
      features: [
        "30–40 min consultation",
        "Nutritional assessment",
        "Goal-setting + diet advice",
        "1 follow-up message (within 7 days)",
        "Next-step guidance",
      ],
      idealFor: "First-time clients seeking clarity before starting a long plan.",
      buttonText: "BOOK CONSULTATION",
      isFeatured: false ,
    },
    
    {
      icon: <FaDumbbell size={26} />,
      title: "100 DAYS DIET PROGRAM",
      subtitle: "Transform your metabolism sustainably.",
      price: "10999",
      priceDetails: "/100 days",
      features: [
        "Detailed nutritional assessment",
        "Custom 100-day diet plan",
        "Explanation call with family",
        "Daily accountability",
        "Fortnightly reviews",
        "Lifestyle-based plan changes",
      ],
      idealFor: "Clients seeking long-term transformation & balance.",
      buttonText: "JOIN 100 DAYS PROGRAM",
      isFeatured: true,
    },
  ];

  const handleButtonClick = () => {
    if (onOpenEnquiryForm) {
      onOpenEnquiryForm();
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section style={styles.section}>
      <div style={styles.container}>
        {isMobile ? (
          <div style={styles.carouselContainer}>
            <div style={styles.carouselWrapper}>
              <div 
                style={{
                  ...styles.carouselTrack,
                  transform: `translateX(-${currentSlide * 100}%)`,
                }}
              >
                {pricingPlans.map((plan, i) => (
                  <div key={i} style={styles.carouselSlide}>
                    <PricingCard 
                      plan={plan} 
                      handleButtonClick={handleButtonClick}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.dotsContainer}>
              {pricingPlans.map((_, index) => (
                <button
                  key={index}
                  style={{
                    ...styles.dot,
                    ...(currentSlide === index ? styles.activeDot : {}),
                  }}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div style={styles.wrapper}>
            {pricingPlans.map((plan, i) => (
              <PricingCard 
                key={i}
                plan={plan} 
                handleButtonClick={handleButtonClick}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const PricingCard = ({ plan, handleButtonClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        ...styles.card,
        ...(plan.isFeatured ? styles.featuredCard : styles.regularCard),
        transform: isHovered ? "translateY(-8px)" : "translateY(0)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{
        ...styles.iconBox,
        ...(plan.isFeatured ? styles.featuredIconBox : styles.regularIconBox)
      }}>
        {plan.icon}
      </div>

      <h2 style={{
        ...styles.title,
        ...(plan.isFeatured ? styles.featuredTitle : styles.regularTitle)
      }}>
        {plan.title}
      </h2>
      <p style={{
        ...styles.subtitle,
        ...(plan.isFeatured ? styles.featuredSubtitle : styles.regularSubtitle)
      }}>
        {plan.subtitle}
      </p>

      <div style={styles.priceBox}>
        <span style={{
          ...styles.currency,
          ...(plan.isFeatured ? styles.featuredCurrency : styles.regularCurrency)
        }}>
          ₹
        </span>
        <span style={{
          ...styles.price,
          ...(plan.isFeatured ? styles.featuredPrice : styles.regularPrice)
        }}>
          {plan.price}
        </span>
        <span style={{
          ...styles.duration,
          ...(plan.isFeatured ? styles.featuredDuration : styles.regularDuration)
        }}>
          {plan.priceDetails}
        </span>
      </div>

      <ul style={styles.list}>
        {plan.features.map((f, index) => (
          <li key={index} style={{
            ...styles.listItem,
            ...(plan.isFeatured ? styles.featuredListItem : styles.regularListItem)
          }}>
            ✓ {f}
          </li>
        ))}
      </ul>

      <div style={{
        ...styles.idealBlock,
        ...(plan.isFeatured ? styles.featuredIdealBlock : styles.regularIdealBlock)
      }}>
        <p style={{
          ...styles.idealTitle,
          ...(plan.isFeatured ? styles.featuredIdealTitle : styles.regularIdealTitle)
        }}>
          Ideal for
        </p>
        <p style={{
          ...styles.idealText,
          ...(plan.isFeatured ? styles.featuredIdealText : styles.regularIdealText)
        }}>
          {plan.idealFor}
        </p>
      </div>

      <InteractiveHoverButton 
        onClick={handleButtonClick}
        isFeatured={plan.isFeatured}
      >
        {plan.buttonText}
      </InteractiveHoverButton>
    </div>
  );
};

const InteractiveHoverButton = ({ children, onClick, isFeatured }) => {
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  return (
    <button
      style={{
        ...styles.button,
        ...(isFeatured ? styles.featuredButton : styles.regularButton),
        position: "relative",
        overflow: "hidden",
      }}
      onClick={onClick}
      onMouseEnter={() => setIsButtonHovered(true)}
      onMouseLeave={() => setIsButtonHovered(false)}
    >
      <div style={styles.buttonContent}>
        <div style={{
          ...styles.buttonDot,
          ...(isFeatured ? styles.featuredButtonDot : styles.regularButtonDot),
          transform: isButtonHovered ? "scale(80)" : "scale(1)",
        }}></div>
        <span style={{
          ...styles.buttonText,
          transform: isButtonHovered ? "translateX(50px)" : "translateX(0)",
          opacity: isButtonHovered ? 0 : 1,
        }}>
          {children}
        </span>
      </div>
      <div style={{
        ...styles.buttonHoverContent,
        ...(isFeatured ? styles.featuredButtonHoverText : styles.regularButtonHoverText),
        transform: isButtonHovered ? "translateX(-20px)" : "translateX(50px)",
        opacity: isButtonHovered ? 1 : 0,
      }}>
        <span>{children}</span>
        <ArrowRight size={18} />
      </div>
    </button>
  );
};

const styles = {
  section: {
    background: "#f9fafb",
    padding: "60px 20px",
    display: "flex",
    justifyContent: "center",
  },
  container: {
    width: "100%",
    maxWidth: "1200px",
  },
  wrapper: {
    display: "flex",
    gap: "20px",
    width: "100%",
    justifyContent: "center",
    flexWrap: "nowrap",
    alignItems: "stretch",
  },
  carouselContainer: {
    position: "relative",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
  },
  carouselWrapper: {
    width: "100%",
    maxWidth: "500px",
    overflow: "hidden",
    position: "relative",
  },
  carouselTrack: {
    display: "flex",
    transition: "transform 0.4s ease-in-out",
    width: "100%",
  },
  carouselSlide: {
    minWidth: "100%",
    display: "flex",
    justifyContent: "center",
    padding: "0 10px",
    boxSizing: "border-box",
  },
  dotsContainer: {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
    marginTop: "20px",
  },
  dot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    background: "#d1d5db",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    padding: 0,
  },
  activeDot: {
    background: "#009e69",
    width: "32px",
    borderRadius: "6px",
  },
  card: {
    borderRadius: "14px",
    padding: "25px 20px",
    width: "100%",
    maxWidth: "380px",
    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
    transition: "0.3s ease",
    display: "flex",
    flexDirection: "column",
    minHeight: "520px",
    flex: "1",
  },
  regularCard: {
    background: "#ffffff",
  },
  featuredCard: {
    background: "#009e69",
    color: "#ffffff",
  },
  iconBox: {
    width: "52px",
    height: "52px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
  },
  regularIconBox: {
    background: "#e8f5ea",
    color: "#009e69",
  },
  featuredIconBox: {
    background: "rgba(255, 255, 255, 0.2)",
    color: "#ffffff",
  },
  title: {
    fontSize: "1.1rem",
    fontWeight: "700",
    marginBottom: "10px",
    lineHeight: "1.3",
  },
  regularTitle: {
    color: "#1f2937",
  },
  featuredTitle: {
    color: "#ffffff",
  },
  subtitle: {
    fontSize: "0.9rem",
    marginBottom: "20px",
    lineHeight: "1.4",
  },
  regularSubtitle: {
    color: "#6b7280",
  },
  featuredSubtitle: {
    color: "rgba(255, 255, 255, 0.9)",
  },
  priceBox: {
    display: "flex",
    alignItems: "baseline",
    gap: "4px",
    marginBottom: "20px",
  },
  currency: {
    fontSize: "1.1rem",
    fontWeight: "600",
  },
  regularCurrency: {
    color: "#009e69",
  },
  featuredCurrency: {
    color: "#ffffff",
  },
  price: {
    fontSize: "2.1rem",
    fontWeight: "800",
  },
  regularPrice: {
    color: "#111827",
  },
  featuredPrice: {
    color: "#ffffff",
  },
  duration: {
    fontSize: "0.9rem",
  },
  regularDuration: {
    color: "#6b7280",
  },
  featuredDuration: {
    color: "rgba(255, 255, 255, 0.8)",
  },
  list: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 20px",
    flex: "1",
  },
  listItem: {
    fontSize: "0.9rem",
    marginBottom: "8px",
    lineHeight: "1.4",
  },
  regularListItem: {
    color: "#374151",
  },
  featuredListItem: {
    color: "rgba(255, 255, 255, 0.95)",
  },
  idealBlock: {
    padding: "14px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  regularIdealBlock: {
    background: "#f3f4f6",
  },
  featuredIdealBlock: {
    background: "rgba(255, 255, 255, 0.15)",
  },
  idealTitle: {
    fontWeight: "600",
    fontSize: "0.9rem",
    marginBottom: "6px",
  },
  regularIdealTitle: {
    color: "#111827",
  },
  featuredIdealTitle: {
    color: "#ffffff",
  },
  idealText: {
    fontSize: "0.82rem",
    lineHeight: "1.4",
  },
  regularIdealText: {
    color: "#4b5563",
  },
  featuredIdealText: {
    color: "rgba(255, 255, 255, 0.9)",
  },
  button: {
    border: "none",
    padding: "14px",
    borderRadius: "9999px",
    fontSize: "0.95rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    width: "100%",
    position: "relative",
    overflow: "hidden",
  },
  regularButton: {
    background: "#ffffff",
    color: "#009e69",
    border: "2px solid #009e69",
  },
  featuredButton: {
    background: "#ffffff",
    color: "#009e69",
    border: "2px solid #ffffff",
  },
  buttonContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  buttonDot: {
    height: "8px",
    width: "8px",
    borderRadius: "50%",
    transition: "all 0.3s ease",
  },
  regularButtonDot: {
    background: "#009e69",
  },
  featuredButtonDot: {
    background: "#009e69",
  },
  buttonText: {
    display: "inline-block",
    transition: "all 0.3s ease",
  },
  buttonHoverContent: {
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 10,
    display: "flex",
    height: "100%",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "all 0.3s ease",
  },
  regularButtonHoverText: {
    color: "#ffffff",
  },
  featuredButtonHoverText: {
    color: "#ffffff",
  },
};

export default Pricing;
